import { useId, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useGetActiveRolesByAccountId } from "../../hooks/useRoles";
import type { EventResult } from "../../domain/event.interface";
import { safeColor } from "../../utils/safeColor";
import {
	COMPARATOR_LABEL,
	FREQUENCY_LABEL,
	MeasurementComparator,
	MeasurementFrequency,
	MeasurementProducedBy,
	MeasurementQuantity,
	MeasurementUnit,
	PRODUCED_BY_LABEL,
	QUANTITY_LABEL,
	UNIT_LABEL,
	derivedMeasurementName,
} from "../../domain/measurement.interface";
import type { Measurement } from "../../domain/measurement.interface";

export type { Measurement } from "../../domain/measurement.interface";

interface Props {
	measurements: Measurement[];
	onChange: (measurements: Measurement[]) => void;
	/**
	 * The event's configured results — AC2.
	 *
	 * 🔴 Passed in rather than re-authored. A discrete-outcome measurement's
	 * categories ARE the event's results, with the colours already set for them
	 * (FR-27): "the categories keep taking their labels and colours from the
	 * event's already-configured results and are never retyped."
	 */
	eventResults?: EventResult[];
}

const emptyDraft = (position: number): Measurement => ({
	name: "",
	countedNoun: "",
	quantity: MeasurementQuantity.PERCENTAGE_IN_RANGE,
	unit: MeasurementUnit.PERCENT,
	frequency: MeasurementFrequency.PER_PROTOCOL_DAY,
	comparator: MeasurementComparator.NONE,
	producedBy: MeasurementProducedBy.SYSTEM,
	accessRequirement: "",
	position,
});

/**
 * What an event measures — SIMS-8531.
 *
 * Built to the idiom of the rejection-reasons block next door: a card appended
 * to the event form, holding its own draft state and handing the whole list
 * back through `onChange`.
 *
 * 🔴 Every choice is a Select over a closed list (AC5) — there is no free-text
 * path to quantity, unit, frequency, comparator or producedBy. The server
 * refuses an unrecognised value rather than substituting one (AC10), so a text
 * box here would only produce saves that fail.
 *
 * 🔴 THERE IS NO TARGET VALUE FIELD (AC11). The comparator says a target
 * applies; the number comes from the plan's reward condition. The form states
 * that where the comparator is chosen, because a configurator who expects to
 * type 80 needs to be told where 80 lives, not left looking for the box.
 */
export const MeasurementsSection = ({
	measurements,
	onChange,
	eventResults = [],
}: Props) => {
	// Per-instance so two of these on one page cannot emit duplicate ids, which
	// would break every `htmlFor` / `aria-labelledby` association on the second.
	const uid = useId();
	const [draft, setDraft] = useState<Measurement | null>(null);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);

	// AC13 — the access requirement lists the roles configured for THIS account.
	// No new catalog is introduced for measurements (PO ruling 2026-08-26).
	const { data: roles = [], isLoading: rolesLoading } =
		useGetActiveRolesByAccountId();

	const roleName = (id?: string): string | undefined =>
		roles.find((role) => role.id === id)?.name;

	/** AC3 — the list is ordered by the position the configurator set. */
	const ordered = [...measurements].sort(
		(a, b) => (a.position ?? 0) - (b.position ?? 0),
	);

	const startAdd = (): void => {
		setEditingIndex(null);
		setDraft(emptyDraft(measurements.length));
	};

	const startEdit = (measurement: Measurement): void => {
		setEditingIndex(measurements.indexOf(measurement));
		setDraft({ ...measurement });
	};

	const cancel = (): void => {
		setDraft(null);
		setEditingIndex(null);
	};

	const commit = (): void => {
		if (!draft) return;

		// Blank optional text is dropped rather than stored as "", so an unnamed
		// measurement derives its name (AC12) instead of rendering as empty.
		const cleaned: Measurement = {
			...draft,
			name: draft.name?.trim() ? draft.name.trim() : undefined,
			countedNoun: draft.countedNoun?.trim()
				? draft.countedNoun.trim()
				: undefined,
			accessRequirement: draft.accessRequirement || undefined,
			position: Number.isFinite(draft.position) ? draft.position : 0,
		};

		const next =
			editingIndex === null
				? [...measurements, cleaned]
				: measurements.map((m, i) => (i === editingIndex ? cleaned : m));

		onChange(next);
		cancel();
	};

	/** AC7 — removing one takes it out of the list. */
	const remove = (measurement: Measurement): void => {
		onChange(measurements.filter((m) => m !== measurement));
		if (editingIndex !== null) cancel();
	};

	const set = <K extends keyof Measurement>(
		key: K,
		value: Measurement[K],
	): void =>
		setDraft((current) => (current ? { ...current, [key]: value } : current));

	return (
		<Card variant="outlined" data-testid="measurements-section">
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Measurements
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					What this event produces, so every surface can present it the same way
					without configuring each screen.
				</Typography>

				{ordered.length === 0 && !draft && (
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						No measurements declared.
					</Typography>
				)}

				{ordered.map((measurement) => {
					const title = measurement.name?.trim()
						? measurement.name.trim()
						: derivedMeasurementName(measurement);
					const restriction = roleName(measurement.accessRequirement);

					return (
						<Box
							// 🔴 Keyed by the measurement's own slot, not by position+title.
							// Position is free-text and defaults to the list length, and an
							// unnamed measurement derives its title — so two entries can
							// legitimately share both, and identical keys make React drop or
							// mis-reconcile a row on add/edit/remove. `indexOf` is reference
							// identity, so it is unique per entry.
							key={measurements.indexOf(measurement)}
							sx={{
								display: "flex",
								alignItems: "flex-start",
								justifyContent: "space-between",
								gap: 1,
								py: 1.5,
								borderBottom: "1px solid",
								borderColor: "divider",
							}}
						>
							<Box sx={{ minWidth: 0 }}>
								<Typography
									variant="body2"
									sx={{ fontWeight: 600 }}
									data-testid="measurement-title"
								>
									{title}
									{!measurement.name?.trim() && (
										// AC12 — an unnamed measurement still shows a name, and
										// it is marked so nobody mistakes the derived words for
										// something that was typed.
										<Typography
											component="span"
											variant="caption"
											color="text.secondary"
											sx={{ ml: 1 }}
										>
											(derived)
										</Typography>
									)}
								</Typography>

								<Box
									sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}
								>
									<Chip
										size="small"
										label={QUANTITY_LABEL[measurement.quantity]}
									/>
									<Chip size="small" label={UNIT_LABEL[measurement.unit]} />
									<Chip
										size="small"
										label={FREQUENCY_LABEL[measurement.frequency]}
									/>
									{/* AC1 / AC4 — which comparator applies, and never a value. */}
									<Chip
										size="small"
										variant="outlined"
										label={COMPARATOR_LABEL[measurement.comparator]}
									/>
									<Chip
										size="small"
										variant="outlined"
										label={PRODUCED_BY_LABEL[measurement.producedBy]}
									/>
									{/* AC6 — the list shows which requirement applies. */}
									{measurement.accessRequirement && (
										<Chip
											size="small"
											color="warning"
											variant="outlined"
											label={`Restricted: ${restriction ?? measurement.accessRequirement}`}
										/>
									)}
								</Box>

								{/* AC1's third clause — said on the row, not only in the
								    editor, because this is where a configurator looks for
								    the number they expected to type. */}
								{measurement.comparator !== MeasurementComparator.NONE && (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ display: "block", mt: 0.5 }}
									>
										The target's value comes from the treatment plan's reward
										condition, not from here.
									</Typography>
								)}

								{/* AC2 — a discrete outcome's categories are the event's own
								    results, in their configured colours. */}
								{measurement.quantity ===
									MeasurementQuantity.DISCRETE_OUTCOME && (
									<Box sx={{ mt: 0.75 }}>
										{eventResults.length === 0 ? (
											<Typography variant="caption" color="text.secondary">
												This event has no results configured yet, so this
												measurement has no categories.
											</Typography>
										) : (
											<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
												{eventResults.map((result) => (
													<Chip
														key={result.id}
														size="small"
														label={result.display}
														sx={{
															bgcolor: safeColor(result.displayColor),
															color: safeColor(result.displayColor)
																? "common.white"
																: undefined,
														}}
													/>
												))}
											</Box>
										)}
									</Box>
								)}
							</Box>

							<Box sx={{ display: "flex", flexShrink: 0 }}>
								<Tooltip title="Edit this measurement">
									<IconButton
										size="small"
										aria-label={`Edit ${title}`}
										onClick={() => startEdit(measurement)}
									>
										<EditOutlinedIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Remove this measurement from the event">
									<IconButton
										size="small"
										aria-label={`Remove ${title}`}
										onClick={() => remove(measurement)}
									>
										<DeleteOutlineIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
					);
				})}

				{draft && (
					<Box sx={{ mt: 2 }} data-testid="measurement-draft">
						<Divider sx={{ mb: 2 }} />
						<Grid container spacing={2}>
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									size="small"
									id={`${uid}-measurement-name`}
									label="Name (optional)"
									helperText="Leave blank and every surface derives the same name."
									value={draft.name ?? ""}
									onChange={(e) => set("name", e.target.value)}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel id={`${uid}-measurement-quantity`}>
										Quantity
									</InputLabel>
									<Select
										labelId={`${uid}-measurement-quantity`}
										label="Quantity"
										value={draft.quantity}
										onChange={(e) =>
											set("quantity", e.target.value as MeasurementQuantity)
										}
									>
										{Object.values(MeasurementQuantity).map((value) => (
											<MenuItem key={value} value={value}>
												{QUANTITY_LABEL[value]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel id={`${uid}-measurement-unit`}>Unit</InputLabel>
									<Select
										labelId={`${uid}-measurement-unit`}
										label="Unit"
										value={draft.unit}
										onChange={(e) =>
											set("unit", e.target.value as MeasurementUnit)
										}
									>
										{Object.values(MeasurementUnit).map((value) => (
											<MenuItem key={value} value={value}>
												{UNIT_LABEL[value]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							{/* AC12 — asked for only where it means something. A count with
							    no counted noun renders as a bare number: "181", not
							    "181 visits". */}
							{draft.unit === MeasurementUnit.COUNT && (
								<Grid item xs={12} sm={6}>
									<TextField
										fullWidth
										size="small"
										id={`${uid}-measurement-counted-noun`}
										label="What is being counted"
										placeholder="visits"
										helperText="Without this the figure shows as a bare number."
										value={draft.countedNoun ?? ""}
										onChange={(e) => set("countedNoun", e.target.value)}
									/>
								</Grid>
							)}

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel id={`${uid}-measurement-frequency`}>
										How often it is taken
									</InputLabel>
									<Select
										labelId={`${uid}-measurement-frequency`}
										label="How often it is taken"
										value={draft.frequency}
										onChange={(e) =>
											set("frequency", e.target.value as MeasurementFrequency)
										}
									>
										{Object.values(MeasurementFrequency).map((value) => (
											<MenuItem key={value} value={value}>
												{FREQUENCY_LABEL[value]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel id={`${uid}-measurement-comparator`}>
										Target
									</InputLabel>
									<Select
										labelId={`${uid}-measurement-comparator`}
										label="Target"
										value={draft.comparator}
										onChange={(e) =>
											set("comparator", e.target.value as MeasurementComparator)
										}
									>
										{Object.values(MeasurementComparator).map((value) => (
											<MenuItem key={value} value={value}>
												{COMPARATOR_LABEL[value]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							{/* 🔴 AC1 / AC11 — where the missing number is explained. Said at
							    the point of choosing, so a configurator is never left
							    hunting for a field that does not exist. */}
							{draft.comparator !== MeasurementComparator.NONE && (
								<Grid item xs={12}>
									<Typography variant="caption" color="text.secondary">
										You are not asked for the target's value here. It comes from
										the treatment plan's reward condition, so a plan whose
										target changes partway through needs no second declaration.
									</Typography>
								</Grid>
							)}

							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel id={`${uid}-measurement-produced-by`}>
										Who produces the value
									</InputLabel>
									<Select
										labelId={`${uid}-measurement-produced-by`}
										label="Who produces the value"
										value={draft.producedBy}
										onChange={(e) =>
											set("producedBy", e.target.value as MeasurementProducedBy)
										}
									>
										{Object.values(MeasurementProducedBy).map((value) => (
											<MenuItem key={value} value={value}>
												{PRODUCED_BY_LABEL[value]}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							{/* AC6 + AC13 — roles configured for this account, and leaving it
							    unset is a valid choice meaning the measurement is not
							    restricted. */}
							<Grid item xs={12} sm={6}>
								<FormControl fullWidth size="small">
									<InputLabel id={`${uid}-measurement-access`}>
										Access requirement (optional)
									</InputLabel>
									<Select
										labelId={`${uid}-measurement-access`}
										label="Access requirement (optional)"
										value={draft.accessRequirement ?? ""}
										disabled={rolesLoading}
										onChange={(e) =>
											set("accessRequirement", e.target.value as string)
										}
									>
										<MenuItem value="">
											<em>Not restricted</em>
										</MenuItem>
										{roles.map((role) => (
											<MenuItem key={role.id} value={role.id}>
												{role.name}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									size="small"
									type="number"
									id={`${uid}-measurement-position`}
									label="Position"
									inputProps={{ min: 0 }}
									value={draft.position}
									onChange={(e) => set("position", Number(e.target.value) || 0)}
								/>
							</Grid>

							<Grid item xs={12}>
								<Button variant="contained" size="small" onClick={commit}>
									{editingIndex === null ? "Add measurement" : "Save changes"}
								</Button>
								<Button size="small" onClick={cancel} sx={{ ml: 1 }}>
									Cancel
								</Button>
							</Grid>
						</Grid>
					</Box>
				)}

				{!draft && (
					<Button size="small" onClick={startAdd} sx={{ mt: 1 }}>
						Add measurement
					</Button>
				)}
			</CardContent>
		</Card>
	);
};

export default MeasurementsSection;
